import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AdminAudienceViewDto } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../../locales/messages";
import { reviewApiService } from "../../../../../utils/api";
import { handleReviewError } from "../../errorHandling";
import {
  HistoryTarget,
  ReviewChangeRequest,
  extractList,
} from "../../types";
import { HistoryTimelineItem } from "../../styled";
import { CR_STATUS_COLORS, CR_STATUS_LABEL_FACTORIES } from "./statusColors";
import { useCrSubmitter } from "./useCrSubmitter";
import { SubmitterPill } from "./SubmitterPill";
import { absoluteTime } from "../CommentsPanel/time";
import {
  isPromotionTagField,
  resolvePromotionTagNames,
} from "./shared/promotionTagDisplay";
import { usePromotionTags } from "../../../../../hooks/usePromotionTags";

interface Props {
  target: HistoryTarget | null;
  onClose: () => void;
  reqOptions: { headers: { Authorization: string } };
  audiences?: AdminAudienceViewDto[];
}

export function CrHistoryDrawer({
  target,
  onClose,
  reqOptions,
  audiences,
}: Props) {
  const { t } = useTranslation();
  const [items, setItems] = useState<ReviewChangeRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const resolveSubmitter = useCrSubmitter(audiences);
  const { promotionTags } = usePromotionTags();
  const isPromotionTag = isPromotionTagField(target?.fieldKey);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response =
          await reviewApiService.gfFlyerReviewAdminControllerGetChangeRequestHistory(
            target.flyerItemId,
            String(target.fieldKey),
            reqOptions,
          );
        if (cancelled) return;
        setItems(extractList<ReviewChangeRequest>(response));
      } catch (error) {
        await handleReviewError(error, t);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [target, reqOptions, t]);

  return (
    <Drawer
      anchor="right"
      open={!!target}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
      data-testid="cr-history-drawer"
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #eee",
        }}
      >
        <Typography variant="h6">
          {t(messages.Change_Request_History())}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ p: 2 }}>
        {target ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {target.fieldKey}
          </Typography>
        ) : null}
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t(messages.No_Change_Requests_Yet())}
          </Typography>
        ) : (
          items.map((item) => (
            <HistoryTimelineItem
              key={item.id}
              data-testid={`history-item-${item.id}`}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ rowGap: 0.5 }}
              >
                <Chip
                  size="small"
                  label={
                    CR_STATUS_LABEL_FACTORIES[item.status]
                      ? t(CR_STATUS_LABEL_FACTORIES[item.status]())
                      : item.status
                  }
                  color={CR_STATUS_COLORS[item.status] || "default"}
                />
                {item.metadata?.restoreOf ? (
                  <Chip
                    size="small"
                    label={t(messages.Reverts_Earlier_Change())}
                    color="info"
                    variant="outlined"
                  />
                ) : null}
                {item.metadata?.restoredByCrId ? (
                  <Chip
                    size="small"
                    label={t(messages.Reverted_By_Later_Change())}
                    variant="outlined"
                  />
                ) : null}
                <SubmitterPill cr={item} resolve={resolveSubmitter} />
                <Typography variant="caption" color="text.secondary">
                  {absoluteTime(
                    (item.submittedAt ?? item.createdAt) as string | Date,
                  )}
                </Typography>
              </Stack>
              {isPromotionTag ? (
                (() => {
                  const names = resolvePromotionTagNames(
                    item.newValue,
                    promotionTags,
                  );
                  return names.length > 0 ? (
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      gap={0.5}
                      sx={{ mt: 0.5 }}
                    >
                      {names.map((name, i) => (
                        <Chip
                          key={`${name}-${i}`}
                          size="small"
                          variant="outlined"
                          label={name}
                          sx={{ height: 22, maxWidth: "100%" }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      sx={{ mt: 0.5 }}
                    >
                      No tags
                    </Typography>
                  );
                })()
              ) : (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mt: 0.5,
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {typeof item.newValue === "object"
                    ? JSON.stringify(item.newValue, null, 2)
                    : String(item.newValue ?? "")}
                </Box>
              )}
            </HistoryTimelineItem>
          ))
        )}
      </Box>
    </Drawer>
  );
}
